import React, { useEffect, useRef } from 'react';
import './index.scss';
import { Field, SitecoreContext, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

type Props = {
  rendering?: {
    fields?: {
      'Kaltura Entry ID': Field<string>;
      'Entry is a Playlist': Field<boolean>;
      'Entry is an Audio File': Field<boolean>;
      Title: Field<string>;
      Description: Field<string>;
      'Audio Placeholder Image': {
        value: {
          src: string;
          alt: string;
        };
      };
      'Video Placeholder Image': {
        value: {
          src: string;
          alt: string;
        };
      };
    };
  };
  sitecoreContext: SitecoreContext & {
    pageEditing: boolean;
    custom: {
      siteConfiguration: {
        kalturaAccountID: string;
        kalturaPlayerID: string;
        kalturaAudioID: string;
      };
      targetHostName: string;
    };
  };
};

type InitOptions = {
  scriptUrl: string;
  scriptTagId: string;
  uiConfId: string | number | undefined;
  accountId: string | undefined;
  entryId: string | undefined;
  isPlaylist: boolean;
  useAudioPlayer: boolean;
  providedKalturaPlayer?: any;
  targetId: string;
};

async function initKalturaPlayer(opts: InitOptions) {
  const {
    scriptUrl,
    scriptTagId,
    uiConfId,
    accountId,
    entryId,
    isPlaylist,
    useAudioPlayer,
    targetId,
  } = opts;
  if (!entryId || !uiConfId || !accountId) throw new Error('Missing Kaltura init params');

  // load kaltura script onto page. Needs to be there first to run Kaltura.setup
  if (typeof window !== 'undefined') {
    const kScript = document.createElement('script');
    kScript.id = scriptTagId;
    kScript.src = scriptUrl;
    kScript.async = true;
    await new Promise<void>((resolve, reject) => {
      kScript.onload = () => resolve();
      kScript.onerror = () => reject(new Error('Failed to load Kaltura script: ' + scriptUrl));

      document.head.appendChild(kScript);
    });
  }

  const win: any = window;
  const Kaltura = win.KalturaPlayer;
  if (!Kaltura || typeof Kaltura.setup !== 'function') {
    throw new Error('Kaltura player not available');
  }

  if (targetId) {
    const player = Kaltura.setup({
      targetId: targetId,
      provider: {
        partnerId: Number(accountId),
        uiConfId: Number(uiConfId),
      },
    });

    if (isPlaylist && !useAudioPlayer) {
      if (typeof player.loadPlaylist === 'function') player.loadPlaylist({ playlistId: entryId });
    } else {
      if (typeof player.loadMedia === 'function') player.loadMedia({ entryId });
    }
    return player;
  }
}

const KalturaEmbeddedMedia = ({ rendering, sitecoreContext }: Props): JSX.Element => {
  const instanceIdRef = useRef<string>('kaltura_' + Math.random().toString(36).slice(2));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<any>(null);

  const kModel = rendering?.fields;
  const kalturaEntryId = kModel?.['Kaltura Entry ID']?.value;
  const useAudioPlayer = !!kModel?.['Entry is an Audio File']?.value;
  const kalturaPlaylistActive = !!kModel?.['Entry is a Playlist']?.value;
  const title = kModel?.Title?.value;
  const description = kModel?.Description?.value;
  const videoPlaceholder = rendering?.fields?.['Video Placeholder Image']?.value;
  const audioPlaceholder = rendering?.fields?.['Audio Placeholder Image']?.value;

  const siteSettings = sitecoreContext?.custom?.siteConfiguration;
  const kalturaPlayerId = siteSettings?.kalturaPlayerID;
  const kalturaAccountId = siteSettings?.kalturaAccountID;
  const kalturaAudioPlayerId = siteSettings?.kalturaAudioID;

  const isExperienceEditor = !!sitecoreContext?.pageEditing;

  useEffect(() => {
    if (!kalturaEntryId) return;

    const uiConfId = useAudioPlayer ? kalturaAudioPlayerId : kalturaPlayerId;
    const scriptUrl = `https://api.irp2.ovp.kaltura.com/p/${kalturaAccountId}/embedPlaykitJs/uiconf_id/${uiConfId}`;
    const scriptTagId = `kaltura-script-${uiConfId}-${instanceIdRef.current}`;

    (async () => {
      try {
        // Ensure the container div has the generated id before init
        if (containerRef.current) {
          containerRef.current.id = instanceIdRef.current;
        }
        const player = await initKalturaPlayer({
          scriptUrl,
          scriptTagId,
          uiConfId,
          accountId: kalturaAccountId,
          entryId: kalturaEntryId,
          isPlaylist: kalturaPlaylistActive,
          useAudioPlayer,
          targetId: instanceIdRef.current,
        });

        playerRef.current = player;
      } catch (e) {
        console.error('Kaltura init error', e);
      }
    })();
  }, [
    kalturaEntryId,
    useAudioPlayer,
    kalturaPlaylistActive,
    kalturaAccountId,
    kalturaPlayerId,
    kalturaAudioPlayerId,
  ]);

  if (!kModel || !kModel['Kaltura Entry ID']?.value) {
    if (isExperienceEditor) {
      return (
        <p>
          Please select a valid Datasource, Kaltura Video ID, or Kaltura Playlist ID as data source
          for Kaltura Video Embed rendering.
        </p>
      );
    }
  }

  if (isExperienceEditor && !kalturaEntryId) {
    return <p>Please enter a Kaltura Video Entry ID.</p>;
  }

  if (
    (isExperienceEditor && !useAudioPlayer && !kalturaPlaylistActive) ||
    (isExperienceEditor && !useAudioPlayer && kalturaPlaylistActive)
  ) {
    return (
      <div style={{ width: '100%', marginBottom: 25 }}>
        <img
          loading="lazy"
          src={videoPlaceholder?.src}
          className="img-responsive"
          alt={videoPlaceholder?.alt}
        />
      </div>
    );
  }

  if (isExperienceEditor && useAudioPlayer && !kalturaPlaylistActive) {
    return (
      <div style={{ width: '100%', marginBottom: 25 }}>
        <img
          loading="lazy"
          src={audioPlaceholder?.src}
          className="img-responsive"
          alt={audioPlaceholder?.alt}
        />
      </div>
    );
  }

  if (isExperienceEditor && useAudioPlayer && kalturaPlaylistActive) {
    return <p>The Audio Player is not configured to use a playlist.</p>;
  }

  if (!kalturaEntryId) {
    return <div className="empty-player" />;
  }

  return (
    <div
      id={instanceIdRef.current}
      ref={containerRef}
      data-title={title}
      data-description={description}
      className={`kaltura-player-component ${
        useAudioPlayer ? 'kaltura-audio-component' : 'kaltura-video-component'
      }`}
    />
  );
};

export default withSitecoreContext()(KalturaEmbeddedMedia);
